import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Plus, Send, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDeleteMyGoalMutation, useMyGoalMutation, useMyPerformanceQuery, useWriteReviewMutation } from '@/client/api/hr/perf';
import {
  GOAL_STATUS_BADGE,
  GOAL_STATUS_LABEL,
  RATING_LABEL,
  REVIEW_STATUS_BADGE,
  REVIEW_STATUS_LABEL,
  type Goal,
  type PerformanceReview,
  type WorkplacePerson,
} from '@/client/types/hr';
import { DeleteConfirmDialog, EmptyState, Panel, ProgressBar, RowMenu, StatusBadge } from '../HumanResource/components/hr-ui';
import { GoalDialog } from '../HumanResource/components/GoalDialog';
import { ReviewText } from '../HumanResource/components/ReviewText';
import { formatDay } from '../HumanResource/components/leave-format';

function ReviewEditor({
  review,
  title,
  onClose,
}: {
  review: PerformanceReview | null;
  title: string;
  onClose: () => void;
}) {
  const write = useWriteReviewMutation();
  const [form, setForm] = useState({ rating: 0, strengths: '', improvements: '' });
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (review) setForm({ rating: review.rating ?? 0, strengths: review.strengths ?? '', improvements: review.improvements ?? '' });
  }, [review]);

  const save = (submit: boolean) => {
    if (!review) return;
    write.mutate(
      { id: review.id, rating: form.rating || null, strengths: form.strengths, improvements: form.improvements, submit },
      {
        onSuccess: () => {
          toast.success(submit ? 'Submitted' : 'Draft saved');
          setConfirming(false);
          if (submit) onClose();
        },
        onError: (e) => toast.error((e as Error).message || 'Could not save the review'),
      },
    );
  };

  return (
    <Dialog open={!!review} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="mb-1.5 text-xs font-medium text-gray-600">Overall rating</p>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm((f) => ({ ...f, rating: n }))} aria-label={RATING_LABEL[n]} title={RATING_LABEL[n]}>
                  <Star className={`h-6 w-6 ${n <= form.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 hover:text-amber-300'}`} />
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-600">{form.rating ? RATING_LABEL[form.rating] : 'Pick a rating'}</span>
            </div>
          </div>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">Strengths: what went well</span>
            <textarea
              rows={4}
              value={form.strengths}
              onChange={(e) => setForm((f) => ({ ...f, strengths: e.target.value }))}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-gray-600">To work on next</span>
            <textarea
              rows={4}
              value={form.improvements}
              onChange={(e) => setForm((f) => ({ ...f, improvements: e.target.value }))}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
            />
          </label>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => save(false)} disabled={write.isPending}>
            Save draft
          </Button>
          <Button type="button" className="gap-1.5" disabled={write.isPending || !form.rating || !(form.strengths.trim() || form.improvements.trim())} onClick={() => setConfirming(true)}>
            <Send className="h-4 w-4" /> Submit
          </Button>
        </DialogFooter>
        <DeleteConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Submit this review?"
          description="Once submitted it cannot be changed. A manager review is shared with the person when the cycle closes."
          actionLabel="Submit"
          danger={false}
          onConfirm={() => save(true)}
        />
      </DialogContent>
    </Dialog>
  );
}

export function PerformanceTab({ me, reports }: { me: string; reports: WorkplacePerson[] }) {
  const perfQ = useMyPerformanceQuery();
  const saveGoal = useMyGoalMutation();
  const deleteGoal = useDeleteMyGoalMutation();
  const [editingGoal, setEditingGoal] = useState<Goal | null | 'new'>(null);
  const [deletingGoal, setDeletingGoal] = useState<Goal | null>(null);
  const [writing, setWriting] = useState<PerformanceReview | null>(null);

  const data = perfQ.data;
  const cycleName = (id: string) => data?.cycles.find((c) => c.id === id)?.name ?? 'Review';
  const cycleStatus = (id: string) => data?.cycles.find((c) => c.id === id)?.status;
  const person = (id: string) => reports.find((p) => p.id === id);

  const toWrite = data
    ? [
        ...data.reviews.filter((r) => r.type === 'self' && r.status === 'draft' && cycleStatus(r.cycleId) === 'active'),
        ...data.toWrite.filter((r) => r.status === 'draft'),
      ]
    : [];
  const shared = (data?.reviews ?? []).filter((r) => r.type === 'manager' || r.status === 'submitted');

  if (perfQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white py-14 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your goals and reviews…
      </div>
    );
  }
  if (perfQ.isError || !data) {
    return <EmptyState label={`Could not load performance: ${(perfQ.error as Error)?.message ?? 'unknown error'}`} />;
  }

  const editorTitle = writing
    ? writing.employeeId === me
      ? `Your self review · ${cycleName(writing.cycleId)}`
      : `Review of ${person(writing.employeeId)?.name ?? 'your report'} · ${cycleName(writing.cycleId)}`
    : '';

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title={`Reviews to write (${toWrite.length})`} className="lg:col-span-2">
        {toWrite.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">Nothing to write right now. Reviews open when HR starts a review cycle.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {toWrite.map((r) => {
              const mine = r.employeeId === me;
              const due = data.cycles.find((c) => c.id === r.cycleId)?.dueDate;
              return (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                  <div>
                    <p className="text-sm text-gray-900">{mine ? 'Your self review' : `Review of ${person(r.employeeId)?.name ?? 'your report'}`}</p>
                    <p className="text-xs text-gray-500">
                      {cycleName(r.cycleId)}
                      {due && ` · due ${formatDay(due)}`}
                      {(r.rating || r.strengths || r.improvements) && ' · draft started'}
                    </p>
                  </div>
                  <Button size="sm" onClick={() => setWriting(r)}>
                    {r.rating || r.strengths ? 'Continue' : 'Write'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel
        title="My goals"
        action={
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditingGoal('new')}>
            <Plus className="h-3.5 w-3.5" /> Add goal
          </Button>
        }
      >
        {data.goals.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">No goals yet. Add what you are working towards.</p>
        ) : (
          <ul className="space-y-3">
            {data.goals.map((g) => (
              <li key={g.id} className="rounded-lg border border-gray-100 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{g.title}</p>
                    <p className="text-xs text-gray-500">
                      {g.dueDate ? `Due ${formatDay(g.dueDate)}` : 'No due date'}
                      {g.cycleId && ` · ${cycleName(g.cycleId)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <StatusBadge label={GOAL_STATUS_LABEL[g.status]} className={GOAL_STATUS_BADGE[g.status]} />
                    <RowMenu
                      items={[
                        { label: 'Update', icon: Pencil, onSelect: () => setEditingGoal(g) },
                        { label: 'Delete', icon: Trash2, danger: true, onSelect: () => setDeletingGoal(g) },
                      ]}
                    />
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <ProgressBar value={g.progress} />
                  <span className="text-xs text-gray-500">{g.progress}%</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Shared with me">
        {shared.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">Your manager's review appears here once a review cycle closes.</p>
        ) : (
          <div className="space-y-4">
            {shared.map((r) => (
              <div key={r.id} className="rounded-lg border border-gray-100 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900">
                    {r.type === 'manager' ? "Manager's review" : 'My self review'} · {cycleName(r.cycleId)}
                  </p>
                  <StatusBadge label={REVIEW_STATUS_LABEL[r.status]} className={REVIEW_STATUS_BADGE[r.status]} />
                </div>
                <ReviewText review={r} />
              </div>
            ))}
          </div>
        )}
      </Panel>

      {data.reportGoals.length > 0 && (
        <Panel title="My team's goals" className="lg:col-span-2">
          <ul className="divide-y divide-gray-50">
            {data.reportGoals.map((g) => (
              <li key={g.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm text-gray-900">{g.title}</p>
                  <p className="text-xs text-gray-500">{person(g.employeeId)?.name ?? 'Team member'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{g.progress}%</span>
                  <StatusBadge label={GOAL_STATUS_LABEL[g.status]} className={GOAL_STATUS_BADGE[g.status]} />
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <ReviewEditor review={writing} title={editorTitle} onClose={() => setWriting(null)} />
      <GoalDialog
        open={editingGoal !== null}
        goal={editingGoal === 'new' ? null : editingGoal}
        cycles={data.cycles}
        busy={saveGoal.isPending}
        onClose={() => setEditingGoal(null)}
        onSave={(body) =>
          saveGoal.mutate(body, {
            onSuccess: () => {
              toast.success(body.id ? 'Goal updated' : 'Goal added');
              setEditingGoal(null);
            },
            onError: (e) => toast.error((e as Error).message || 'Could not save the goal'),
          })
        }
      />
      <DeleteConfirmDialog
        open={!!deletingGoal}
        onOpenChange={(v) => !v && setDeletingGoal(null)}
        title="Delete this goal?"
        description={`"${deletingGoal?.title}" will be deleted for good.`}
        onConfirm={() => {
          if (deletingGoal) deleteGoal.mutate(deletingGoal.id, { onSuccess: () => toast.success('Goal deleted'), onError: (e) => toast.error((e as Error).message) });
          setDeletingGoal(null);
        }}
      />
    </div>
  );
}
